import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { SupplierQualification } from '../../lib/api';
import { format, isValid } from 'date-fns';

interface ApprovalsTabProps {
  qualification: SupplierQualification;
  /** Rendered directly beneath the entry it answers, so the exchange reads as
   *  a thread: the customer's question, then the box to answer it. Supplying
   *  this is what turns the history from a log into a conversation. */
  replySlot?: React.ReactNode;
}

/**
 * Safely format a date string, returning fallback if invalid.
 */
function formatDate(dateString: string | undefined | null, formatStr: string, fallback = '—'): string {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (!isValid(date)) return fallback;
  return format(date, formatStr);
}

export function ApprovalsTab({ qualification, replySlot }: ApprovalsTabProps) {
  const approvals = qualification.approvals || [];
  // The reply belongs under the LAST outstanding request, not the first: if the
  // customer has asked twice, it is the most recent question being answered.
  const lastRequestIdx = approvals.reduce(
    (last, a, i) =>
      (a.decision === 'more_info_required' || a.decision === 'request_info') ? i : last,
    -1,
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Approval History</CardTitle>
        <CardDescription>Record of all approval decisions</CardDescription>
      </CardHeader>
      <CardContent>
        {approvals.length > 0 ? (
          <div className="space-y-4">
            {approvals.map((approval, idx) => (
              <div key={approval.id}>
              <div
                key={approval.id}
                className={`p-4 rounded-lg border ${
                  approval.decision === 'approved'
                    ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-900'
                    : approval.decision === 'rejected'
                    ? 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-900'
                    : 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-900'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {approval.decision === 'approved' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                    {approval.decision === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                    {(approval.decision === 'more_info_required' || approval.decision === 'request_info') && (
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                    )}
                    <span className="font-medium capitalize">{approval.decision?.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(approval.reviewed_at || approval.assigned_at, 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
                <p className="text-sm mt-2">By {approval.approver_name || 'Unknown'}</p>
                {approval.comments && (
                  <p className="text-sm mt-2 text-muted-foreground">{approval.comments}</p>
                )}
              </div>
              {replySlot && idx === lastRequestIdx && (
                <div className="mt-3">{replySlot}</div>
              )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No approval decisions yet</p>
        )}
      </CardContent>
    </Card>
  );
}
